import React, { useMemo, useState } from "react";
import { Table, Tag, Tooltip, Button, Badge, Input, Popover } from "antd";
import { Radio, Edit, Power, PowerOff, Copy, Play, FileText, Trash2 } from "lucide-react";
import { getSignalTypeLabel, BrokerAccountsDisplay } from "../utils";
import SignalLogsModal from "./SignalLogsModal";

const SignalDashboard = ({
	signals,
	strategies,
	loading,
	handleEdit,
	handleDeactivate,
	handleActivate,
	getSignalWebhookUrl,
	handleManualExecution,
	handleDeleteConfirm,
	getBrokerLabel,
	toast
}) => {
	const [signalSearchText, setSignalSearchText] = useState("");
	const [logsModalVisible, setLogsModalVisible] = useState(false);
	const [selectedSignal, setSelectedSignal] = useState({ id: null, name: "" });

	const dashboardOuterData = useMemo(() => {
		const filteredSignals = (signals || []).filter(signal => {
			if (!signalSearchText.trim()) return true;
			const searchLower = signalSearchText.toLowerCase().trim();
			const signalName = (signal.name || '').toLowerCase();

			if (signalName.includes(searchLower)) return true;

			const strategyLinks = signal.strategyLinks || [];
			const hasMatchingStrategy = strategyLinks.some(link => {
				const strategy = strategies.find(s => s.id === link.strategyId);
				const strategyName = (strategy?.name || '').toLowerCase();
				return strategyName.includes(searchLower);
			});

			return hasMatchingStrategy;
		});

		return filteredSignals.map(signal => {
			const children = (signal.strategyLinks || []).map(link => {
				const strategy = strategies.find(s => s.id === link.strategyId) || {};
				const strategyBrokers = Array.isArray(strategy.brokers)
					? strategy.brokers.map(b => {
						if (typeof b === 'object' && b?.id) {
							return { id: b.id, multiplier: b.multiplier || 1 };
						}
						return { id: b, multiplier: 1 };
					})
					: (strategy.brokers ? [{ id: strategy.brokers, multiplier: 1 }] : []);
				return {
					key: `${signal.id}-${link.strategyId}`,
					strategyName: strategy.name || link.strategyId,
					signalType: link.signalType,
					isMultiLeg: strategy?.singleOrMultiLeg === false,
					brokers: strategyBrokers,
				};
			});
			// Count unique strategies
			const uniqueStrategyIds = new Set((signal.strategyLinks || []).map(link => link.strategyId));
			const strategyCount = uniqueStrategyIds.size;
			return {
				key: signal.id,
				signalId: signal.id,
				signalName: signal.name,
				isActive: signal.isActive,
				childrenRows: children,
				strategyCount: strategyCount
			};
		});
	}, [signals, strategies, signalSearchText]);

	return (
		<div>
			<div className="qt-algo-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<Radio size={20} />
					<h3>Signal Dashboard</h3>
				</div>
				<Input
					placeholder="Search signals or strategies..."
					value={signalSearchText}
					onChange={(e) => setSignalSearchText(e.target.value)}
					allowClear
					style={{ width: 250 }}
					size="small"
				/>
			</div>
			<Table
				dataSource={dashboardOuterData}
				rowKey={(r) => r.signalId}
				size="small"
				pagination={false}
				scroll={{ x: true }}
				expandable={{
					expandedRowRender: (record) => (
						<Table
							dataSource={record.childrenRows}
							rowKey={(r) => r.key}
							pagination={false}
							size="small"
							scroll={{ x: 600 }}
							tableLayout="fixed"
							columns={[
								{ title: 'Strategy', align: "left", dataIndex: 'strategyName', key: 'strategyName' },
								{ title: 'Signal Type', align: "left", dataIndex: 'signalType', key: 'signalType', render: (val) => <Tag color="blue">{getSignalTypeLabel(val)}</Tag> },
								{
									title: 'Brokers', align: "left", key: 'brokers', render: (_, rec) => (
										<BrokerAccountsDisplay brokers={rec.brokers} labelFn={getBrokerLabel} />
									)
								},
							]}
						/>
					),
				}}
				columns={[
					{ title: 'Signal Name', align: "left", dataIndex: 'signalName', key: 'signalName' },
					{ title: 'Strat Count', align: "center", dataIndex: 'strategyCount', key: 'strategyCount', render: (count) => count || 0 },
					{ title: 'Status', align: "left", dataIndex: 'isActive', key: 'status', render: (v) => v ? <Badge color="green" text="Active" /> : <Badge color="red" text="Inactive" /> },
					{
						title: 'Actions', align: "center", key: 'actions', render: (_, rec) => (
							<div className="signal-actions-container">
								<Tooltip title="Edit Signal">
									<Button
										className="edit-strat-btn"
										size="small"
										icon={<Edit size={14} />}
										onClick={() => handleEdit({ id: rec.signalId, name: rec.signalName })}
									/>
								</Tooltip>
								<Tooltip title={rec.isActive ? "Deactivate" : "Activate"}>
									<Button
										className={rec.isActive ? "deactivate-btn" : "activate-btn"}
										size="small"
										icon={rec.isActive ? <PowerOff size={14} /> : <Power size={14} />}
										onClick={() => rec.isActive ? handleDeactivate(rec.signalId) : handleActivate(rec.signalId)}
									/>
								</Tooltip>
								<Tooltip title="Copy Webhook">
									<Button
										className="copy-webhook-btn"
										size="small"
										icon={<Copy size={14} />}
										onClick={async () => { 
											const wh = await getSignalWebhookUrl(rec.signalId); 
											if (wh?.code === 200) { 
												navigator.clipboard.writeText(wh.data); 
												toast.success('Webhook copied'); 
											} 
										}}
									/>
								</Tooltip>
								<Tooltip title="Manual Trigger">
									<Button
										className="manual-execution-btn"
										size="small"
										icon={<Play size={14} />}
										onClick={() => handleManualExecution(rec.signalId)}
										loading={loading}
									/>
								</Tooltip>
								<Tooltip title="Logs">
									<Button
										className="logs-btn"
										size="small"
										icon={<FileText size={14} />}
										onClick={() => {
											setSelectedSignal({ id: rec.signalId, name: rec.signalName });
											setLogsModalVisible(true);
										}}
									/>
								</Tooltip>
								<Tooltip title="Delete">
									<Button
										size="small"
										className="delete-btn"
										danger
										icon={<Trash2 size={14} />}
										onClick={() => handleDeleteConfirm(rec)}
									/>
								</Tooltip>
							</div>
						)
					},
				]}
			/>
			<SignalLogsModal
				visible={logsModalVisible}
				onClose={() => {
					setLogsModalVisible(false);
					setSelectedSignal({ id: null, name: "" });
				}}
				signalId={selectedSignal.id}
				signalName={selectedSignal.name}
			/>
		</div>
	);
};

export default SignalDashboard;
